import useFetch from "hooks/useFetch";
import { useTranslation } from "react-i18next";
import { getToken } from "utils/getToken";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseButtonIcon from "assets/icons/Close";
import CustomButton from "components/Button";
import { useState } from "react";
import CustomInput from "components/Input";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IListedDomain } from "interfaces/domains";
import { toast } from "react-toastify";

interface IListedDomainsModalProps {
  open: boolean;
  setIsModalOpen: (value: boolean) => void;
  setListedDomains: React.Dispatch<React.SetStateAction<IListedDomain[]>>;
}

const AdminListedDomainsModal = ({ open, setIsModalOpen, setListedDomains }: IListedDomainsModalProps) => {
  const token = getToken();
  const { t } = useTranslation();
  const [newDomains, setNewDomains] = useState<string[]>([""]);
  const [inputErrors, setInputErrors] = useState<string[]>([""]);
  const domainNameRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;

  const { loading, triggerFetch: addNewListedDomains } = useFetch({
    url: "admin/domains",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleDomainInputChange = (index: number, value: string) => {
    const newDomainsArr = [...newDomains];
    newDomainsArr[index] = value;
    setNewDomains(newDomainsArr);
  };

  const handleDomainInputAdd = () => {
    setNewDomains([...newDomains, ""]);
    setInputErrors([...inputErrors, ""]);
  };

  const handleDomainInputDelete = (index: number) => {
    const updatedDomainsArr = [...newDomains];
    updatedDomainsArr.splice(index, 1);
    setNewDomains(updatedDomainsArr);

    const updatedErrorsArr = [...inputErrors];
    updatedErrorsArr.splice(index, 1);
    setInputErrors(updatedErrorsArr);
  };

  const handleDomainsModalSubmit = async () => {
    const checkedDomainNamesArr = newDomains.map((domain) => {
      return domain.trim();
    });

    const updatedErrors = checkedDomainNamesArr.map((domain) =>
      domainNameRegex.test(domain) ? "" : "invalidDomainName"
    );
    setInputErrors(updatedErrors);

    const isErrorsInDomainInputs = updatedErrors.some((error) => error !== "");
    if (isErrorsInDomainInputs) {
      toast.error(t("invalidDomainName"));
      return;
    }

    try {
      const response = await addNewListedDomains({ newDomains });
      if (response?.success) {
        const newListedDomainsArr = response.newListedDomains.reverse();
        setListedDomains((prevState) => {
          return [...newListedDomainsArr, ...prevState];
        });
        toast.success(t("successNewDomainsAddedInfo"));
        setIsModalOpen(false);
      }
    } catch (error) {
      toast.error(t("unseccessfulDomainsAddedInfo"));
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setIsModalOpen(false)}
      aria-labelledby="modal-push-template-title"
      aria-describedby="modal-push-template-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "24px",
          bgcolor: "#FFFFFF",
          boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          width: "90%",
          maxWidth: "720px",
          height: "auto",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 16, right: 16, width: 32, height: 32 }}
          aria-label="modal-close-button"
          onClick={() => setIsModalOpen(false)}
        >
          <CloseButtonIcon />
        </IconButton>
        <Typography
          id="modal-title"
          sx={{
            marginBottom: { xs: "24px", md: "32px" },
            paddingRight: "14px",
            fontSize: { xs: "20px", md: "24px" },
            fontWeight: 400,
            lineHeight: 1.22,
            color: "#2E263D",
          }}
        >
          {t("addNewListedDomains")}
        </Typography>
        <Typography
          id="modal-description"
          sx={{
            marginBottom: { xs: "24px", md: "32px" },
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: 1.35,
            color: "#6d6777",
          }}
        >
          {t("listedDomainModalDescription")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", maxHeight: "300px", overflowY: "auto" }}>
          {newDomains.map((domain, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <CustomInput
                required
                fullWidth
                id="domainName"
                height="40px"
                placeholder={t("enterDomainName")}
                name="domainName"
                value={domain}
                onChange={(event: any) => handleDomainInputChange(index, event.target.value)}
                customBgColor="#F6F7F9"
                helperText={t(inputErrors[index])}
                error={inputErrors[index] ? true : false}
              />
              {index === 0 ? (
                <BootstrapTooltip title={t("addTextField")} placement="top">
                  <IconButton
                    sx={{ height: "40px", padding: 0 }}
                    aria-label="add-icon"
                    id="icon-preview"
                    onClick={handleDomainInputAdd}
                  >
                    <AddCircleOutlineIcon
                      sx={{
                        width: "36px",
                        height: "36px",
                        color: "#F4701B",
                      }}
                    />
                  </IconButton>
                </BootstrapTooltip>
              ) : (
                <BootstrapTooltip title={t("delete")} placement="top">
                  <IconButton
                    sx={{ width: "36px", height: "40px", padding: 0 }}
                    aria-label="add-icon"
                    id="icon-preview"
                    onClick={() => handleDomainInputDelete(index)}
                  >
                    <DeleteOutlineIcon
                      sx={{
                        width: "24px",
                        height: "24px",
                        color: "#F4701B",
                      }}
                    />
                  </IconButton>
                </BootstrapTooltip>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: "center",
            marginTop: { xs: "24px", md: "32px" },
          }}
        >
          <CustomButton
            width="100%"
            height={40}
            textButton={t("cancel")}
            customColor="#6D6777"
            customBorderColor="#C9D0D8"
            customHoverColor="transparent"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={() => setIsModalOpen(false)}
          />
          <CustomButton
            width="100%"
            textButton={t("add")}
            height={40}
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleDomainsModalSubmit}
            loading={loading}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default AdminListedDomainsModal;
